import React, { useState, useEffect, useRef, useMemo } from 'react';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconSearch } from 'icons/indexed-icons/IconSearch';
import { StyledTableInput } from 'components/machine-health/configurator/WidgetTable/Form';
import { ChangeEvent } from 'types';
import {
  DropdownContainer,
  IconWrapper,
  Label,
  List,
  OptionItem,
  OptionItemContainer,
  PopupContainer,
  RedirectToDocCenter,
  SearchBarWrapper,
  SelectedOption,
  StyledSearch
} from './DropdownOption.element';
import theme from 'themes';
import { InformationTooltip } from 'components';
import { JBTRoutes } from 'constants/routes';
import { useHistory } from 'react-router-dom';
import { ToolTipWrapper } from 'components/machine-health/configurator/WidgetTable/Dropdown';

interface CustomDropdownProps {
  options?: OptionType[];
  icon?: string;
  handleOptionSelect?: (option?: OptionType) => void;
  searchable?: boolean;
  searchInputValue?: (input?: string) => void;
  selectedOpt?: string | JSX.Element;
  label?: JSX.Element;
  iconColor?: string;
  containerPosition?: string;
  popupTop?: string;
  width?: string;
  optionLength?: number;
  machineId?: string;
  className?: string;
}

export interface OptionType {
  value: string;
  label: string;
  icon?: JSX.Element;
}

const DropdownOption: React.FC<CustomDropdownProps> = ({
  options,
  icon,
  handleOptionSelect,
  searchable,
  searchInputValue,
  selectedOpt,
  label,
  iconColor,
  popupTop,
  width,
  containerPosition,
  optionLength,
  machineId,
  className
}) => {
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);

  className = className ? `${className} dropdown-option` : 'dropdown-option'; // Add className to the component

  const [searchInput, setSearchInput] = useState('');
  const [filterOption, setFilterOption] = useState(options);

  const dropdownRef = useRef<HTMLDivElement>(null); // Ref for the dropdown container

  // Function to toggle the dropdown visibility
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };
  const handleSearchOption = (e: ChangeEvent) => {
    const value = e.target.value;
    if (value.trim().length === 0) {
      setFilterOption(options);
      setSearchInput('');
      searchInputValue?.('');
      return;
    }

    setSearchInput(value);
    const filteredList = options?.filter(
      (option) =>
        option.label.toLowerCase().includes(value.toLowerCase()) ||
        option.value.toLowerCase().includes(value.toLowerCase())
    );
    setFilterOption(filteredList);
    searchInputValue?.(value);
  };

  useMemo(() => {
    setFilterOption(options);
  }, [options]);

  // Effect to handle clicks outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    // Add event listener when the component mounts
    document.addEventListener('click', handleClickOutside);

    // Clean up the event listener when the component unmounts
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  return (
    <DropdownContainer
      ref={dropdownRef}
      containerPosition={containerPosition}
      className={className}
    >
      <div style={{ cursor: 'pointer', paddingLeft: '4px' }} onClick={toggleDropdown}>
        {label}
      </div>
      {selectedOpt && <SelectedOption>{selectedOpt}</SelectedOption>}
      {icon ? (
        <img src={icon} onClick={toggleDropdown} />
      ) : isOpen ? (
        <FontAwesomeIcon
          icon={faChevronUp}
          style={{ color: iconColor || theme.colors.black, cursor: 'pointer' }}
          onClick={toggleDropdown}
        />
      ) : (
        <FontAwesomeIcon
          icon={faChevronDown}
          style={{ color: iconColor || theme.colors.black, cursor: 'pointer' }}
          onClick={toggleDropdown}
        />
      )}
      {isOpen && (
        <PopupContainer
          className="option-popup"
          width={width}
          top={popupTop || '1rem'}
          height={'24rem'}
          overflow="auto"
        >
          {searchable && (
            <StyledSearch>
              <SearchBarWrapper>
                <IconWrapper>
                  <IconSearch width="15" />
                </IconWrapper>
                <StyledTableInput
                  type="text"
                  value={searchInput}
                  onChange={handleSearchOption}
                  placeholder={'Search'}
                  style={{
                    paddingLeft: '2rem'
                  }}
                  borderVariant={'none'}
                  bgColor="#F2F3F4"
                />
              </SearchBarWrapper>
            </StyledSearch>
          )}
          <List>
            {filterOption?.map((option, i) => (
              <OptionItemContainer
                key={i}
                className="option-item-container"
                onClick={() => {
                  setIsOpen(false);
                  handleOptionSelect && handleOptionSelect(option);
                }}
              >
                {option?.icon}
                <OptionItem key={option.value} padding="0">
                  <ToolTipWrapper offset="center" tooltipMessage={option.label}>
                    <Label>{option.label}</Label>
                  </ToolTipWrapper>
                </OptionItem>
              </OptionItemContainer>
            ))}
          </List>
          {optionLength && optionLength > 10 && (
            <RedirectToDocCenter
              onClick={() => {
                history.push(`/documentation?machineId=${machineId}`);
              }}
            >
              See more...
            </RedirectToDocCenter>
          )}
        </PopupContainer>
      )}
    </DropdownContainer>
  );
};

export default DropdownOption;
